import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import { Card } from '@/components/atoms/Card';
import PageLayout from '@/components/Layout/PageLayout';
import SomethingHappened from '@/components/SomethingHappened';
import { useAuth, usePermissions } from '@/contexts/auth';

import type { UserInviteFormState } from './Components/UserInviteForm';
import UserInviteModal from './Components/UserInviteModal';
import UserSearchField from './Components/UserSearchField';
import UserTable from './Components/UserTable';
import type { Location } from './Components/userTableHelpers';
import { useUserPageData } from './useUserPageData';

import styles from './UserPage.module.scss';

/** One empty list while the client loads, so the table's columns are not rebuilt each render. */
const NO_LOCATIONS: Location[] = [];

interface UserPageProps {
    clientId?: number;
}

function UserPage({ clientId }: UserPageProps) {
    const { clients } = useAuth();
    const { isClientAdmin, isOptoScaleAdmin } = usePermissions();
    const { t } = useTranslation();
    const { userData, isFetching, loadFailed, client, clientList, refetchUsers, ...actions } =
        useUserPageData(clientId);

    const [inviteModalOpen, setInviteModalOpen] = useState(false);
    const [filter, setFilter] = useState('');
    const [formKey, setFormKey] = useState(0);

    const handleInviteUser = async (inputs: UserInviteFormState) => {
        await actions.inviteUser(inputs);
        setInviteModalOpen(false);
        setFormKey((prev) => prev + 1); // Reset form on next open
    };

    const handleModalClose = () => {
        setInviteModalOpen(false);
        setFormKey((prev) => prev + 1); // Reset form on next open
    };

    return (
        <PageLayout title={t('userAccess.user.users')}>
            <Card as="section" padding="xl">
                <div className={styles.toolbar}>
                    <div className={styles.toolbarItem}>
                        {clientList && (
                            <Button
                                variant="primary"
                                onClick={() => setInviteModalOpen(true)}
                                trackingId="user_page_invite_open">
                                {t('userAccess.common.inviteUser')}
                            </Button>
                        )}
                    </div>

                    <div className={styles.toolbarEnd}>
                        <UserSearchField filter={filter} setFilter={setFilter} />
                    </div>
                </div>

                {/* Backend (ensureClientIdAdminAccess) also accepts CLIENT_ADMIN, but
                    the UI intentionally limits this to OptoScale admins only for now. */}
                {loadFailed ? (
                    <SomethingHappened onRetry={refetchUsers} isRetrying={isFetching} />
                ) : (
                    <UserTable
                        users={userData ?? []}
                        canDeleteUsers={!clientId}
                        canImpersonateUsers={!clientId}
                        canManageAccess={!!clientId && isOptoScaleAdmin}
                        isFetching={isFetching}
                        clientId={clientId || 0}
                        clientLocations={client?.locations ?? NO_LOCATIONS}
                        deleteUser={actions.deleteUser}
                        impersonateUser={actions.impersonateUser}
                        onAccessSaved={refetchUsers}
                        filter={filter}
                    />
                )}
            </Card>

            {clientList && (
                <UserInviteModal
                    key={formKey}
                    isOpen={inviteModalOpen}
                    onClose={handleModalClose}
                    client={client}
                    clients={clients}
                    canInviteForOptoscaleAdmin={!clientId}
                    canInviteForClient={!clientId || isClientAdmin(clientId)}
                    onSubmit={handleInviteUser}
                />
            )}
        </PageLayout>
    );
}

export default UserPage;
